/**
 * AI Co-Pilot service layer - the human-in-the-loop action queue.
 *
 * When the AI agent is asked to perform a medium/high-risk write (assign a
 * driver, update insurance, ...), it queues the action as "pending" instead
 * of executing it. These endpoints let a fleet manager review, approve or
 * reject those actions. All calls go through cdnktapi (authenticated axios
 * instance); company scoping is resolved server-side from the session token.
 */
import axios from "./axios";

export type ActionRisk = "low" | "medium" | "high" | "critical";
export type ActionStatus = "pending" | "approved" | "rejected" | "auto_approved";

export interface PendingAction {
  action_id: number;
  action_type: string;
  description: string;
  risk_level: ActionRisk;
  requested_by: string;
  created_at: string;
  payload?: string;
}

export interface ActionHistoryItem {
  action_id: number;
  action_type: string;
  description: string;
  risk_level: ActionRisk;
  status: ActionStatus;
  requested_by: string;
  approved_by: string | null;
  created_at: string;
  approved_at: string | null;
  rejection_reason: string | null;
}

export interface ActionStats {
  pending: number;
  approved: number;
  rejected: number;
  auto_approved: number;
}

export const listPendingActions = async (): Promise<PendingAction[]> => {
  const { data } = await axios.get<{ actions: PendingAction[] }>(
    "/ai-agent/copilot/actions/pending"
  );
  return data.actions ?? [];
};

export const listActionHistory = async (
  limit = 50
): Promise<ActionHistoryItem[]> => {
  const { data } = await axios.get<{ actions: ActionHistoryItem[] }>(
    "/ai-agent/copilot/actions/history",
    { params: { limit } }
  );
  return data.actions ?? [];
};

export const getActionStats = async (): Promise<ActionStats> => {
  const { data } = await axios.get<ActionStats>(
    "/ai-agent/copilot/actions/stats"
  );
  return data;
};

export const approveAction = async (actionId: number): Promise<void> => {
  await axios.post(`/ai-agent/copilot/actions/${actionId}/approve`, {});
};

export const rejectAction = async (
  actionId: number,
  reason = ""
): Promise<void> => {
  await axios.post(`/ai-agent/copilot/actions/${actionId}/reject`, { reason });
};
